import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getNotificationUnreadCount,
  listNotificationInbox,
  markNotificationInboxRead,
  getNotificationPreferences,
  listNotificationEvents,
  updateNotificationPreferences,
  listNotificationEndpoints,
} from '../../services/notifications.service';
import {
  NotificationInboxItem,
  NotificationsInboxListQueryParams,
  NotificationsInboxMarkReadBodyParams,
  NotificationsInboxUnreadCountQueryParams,
  NotificationPreferences,
  NotificationPreferencesUpdateBodyParams,
} from '../../types/notification.types';

interface UseNotificationsInboxResult {
  items: NotificationInboxItem[];
  unreadCount?: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  loadMore: () => void;
  hasNextPage: boolean;
  loadingMore: boolean;
}

export const useNotificationsInbox = (
  queryParams: NotificationsInboxListQueryParams = {},
  enabled: boolean = true
): UseNotificationsInboxResult => {
  const fetchInbox = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }) => {
    return await listNotificationInbox({
      ...queryParams,
      pageToken: pageParam,
      includeUnreadCount: pageParam ? false : true,
    });
  };

  const {
    data,
    error,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['notifications-inbox', queryParams],
    queryFn: fetchInbox,
    getNextPageParam: (lastPage) => lastPage.nextPageToken,
    initialPageParam: undefined,
    enabled,
  });

  const items = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages.flatMap((page) => page.items || []);
  }, [data?.pages]);

  const unreadCount = data?.pages?.[0]?.unreadCount;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    items,
    unreadCount,
    isLoading,
    error: error as Error | null,
    refetch,
    loadMore,
    hasNextPage: hasNextPage || false,
    loadingMore: isFetchingNextPage,
  };
};

export const useNotificationsUnreadCount = (
  queryParams: NotificationsInboxUnreadCountQueryParams = {},
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['notifications-unread-count', queryParams],
    queryFn: () => getNotificationUnreadCount(queryParams),
    enabled,
  });
};

export const useNotificationsPreferences = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['notifications-preferences'],
    queryFn: () => getNotificationPreferences(),
    enabled,
  });
};

export const useNotificationsUpdatePreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: NotificationPreferencesUpdateBodyParams) =>
      updateNotificationPreferences(body),
    onSuccess: (data: NotificationPreferences) => {
      queryClient.setQueryData(['notifications-preferences'], data);
    },
  });
};

export const useNotificationsEvents = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['notifications-events'],
    queryFn: () => listNotificationEvents(),
    enabled,
  });
};

export const useNotificationsEndpoints = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['notifications-endpoints'],
    queryFn: () => listNotificationEndpoints(),
    enabled,
  });
};

export const useNotificationsMarkRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: NotificationsInboxMarkReadBodyParams) =>
      markNotificationInboxRead(body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications-inbox'],
      });
      queryClient.invalidateQueries({
        queryKey: ['notifications-unread-count'],
      });
    },
  });
};
